import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authService, ChangePasswordDto } from '../../services/authService';
import { useNotifications } from '../feedback/NotificationSystem';
import { PersianOverlayLoader } from '../shared/PersianLoader';
import { Eye, EyeOff, Lock } from 'lucide-react';

export interface PasswordChangeFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  className?: string;
  /** `embedded` — no card chrome; parent modal supplies header/footer. */
  variant?: 'card' | 'embedded';
  formId?: string;
  hideActions?: boolean;
}

interface FormData {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

interface ValidationErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmNewPassword?: string;
}

export const PasswordChangeForm: React.FC<PasswordChangeFormProps> = ({
  onSuccess,
  onCancel,
  className = '',
  variant = 'card',
  formId = 'profile-change-password-form',
  hideActions = false,
}) => {
  const { showSuccess, showError } = useNotifications();
  const { t } = useTranslation('auth');
  const [formData, setFormData] = useState<FormData>({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Current password validation
    if (!formData.currentPassword.trim()) {
      newErrors.currentPassword = t('changePassword.currentRequired');
    }

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = t('changePassword.newRequired');
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = t('changePassword.newMinLength');
    } else if (formData.newPassword === formData.currentPassword) {
      newErrors.newPassword = t('changePassword.newMustDiffer');
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(formData.newPassword)) {
      newErrors.newPassword = t('changePassword.newComplexity');
    }

    if (!formData.confirmNewPassword.trim()) {
      newErrors.confirmNewPassword = t('changePassword.confirmRequired');
    } else if (formData.confirmNewPassword !== formData.newPassword) {
      newErrors.confirmNewPassword = t('changePassword.passwordsMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error for this field when user starts typing
    if (errors[name as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const passwordData: ChangePasswordDto = {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmNewPassword: formData.confirmNewPassword
      };

      const response = await authService.changePassword(passwordData);

      if (response.success) {
        showSuccess(t('changePassword.success'), {
          title: t('changePassword.successTitle'),
          duration: 5000,
          context: 'password_change'
        });

        // Reset form
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmNewPassword: ''
        });

        // Call success callback
        if (onSuccess) {
          onSuccess();
        }
      } else {
        showError(response.message || t('changePassword.failed'), {
          title: t('changePassword.failedTitle'),
          duration: 8000,
          context: 'password_change'
        });
      }
    } catch (error) {
      console.error('Password change error:', error);

      let errorMessage = t('changePassword.failed');

      if (error instanceof Error) {
        const originalMessage = error.message.toLowerCase();

        if (originalMessage.includes('current password') || originalMessage.includes('incorrect')) {
          errorMessage = t('changePassword.currentIncorrect');
        } else if (originalMessage.includes('password') && (originalMessage.includes('requirement') || originalMessage.includes('symbol') || originalMessage.includes('character'))) {
          errorMessage = t('changePassword.newComplexity');
        } else if (originalMessage.includes('unauthorized') || originalMessage.includes('401')) {
          errorMessage = t('changePassword.sessionExpired');
        } else if (originalMessage.includes('validation') || originalMessage.includes('invalid')) {
          errorMessage = t('changePassword.validationFailed');
        } else {
          errorMessage = t('changePassword.genericFailed');
        }
      }

      showError(errorMessage, {
        title: t('changePassword.failedTitle'),
        duration: 10000,
        persistent: true,
        context: 'password_change'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset form
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: ''
    });
    setErrors({});

    if (onCancel) {
      onCancel();
    }
  };

  const isEmbedded = variant === 'embedded';
  const showCardChrome = !isEmbedded;
  const showInlineActions = !hideActions && !isEmbedded;

  return (
    <>
      {/* Global Persian Loader Overlay */}
      <PersianOverlayLoader
        isVisible={isSubmitting}
        message={t('changePassword.changing')}
        variant="ornate"
      />

      <div
        className={
          showCardChrome
            ? `bg-white dark:bg-gray-900 rounded-xl shadow-md dark:shadow-none dark:ring-1 dark:ring-gray-700 p-6 ${className}`
            : `px-4 py-4 sm:px-5 sm:py-5 ${className}`
        }
      >
        {showCardChrome && (
          <div className="flex items-center space-x-3 mb-6">
            <Lock className="w-6 h-6 text-blue-600 dark:text-blue-400 shrink-0" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('changePassword.title')}</h2>
          </div>
        )}

        <form id={formId} onSubmit={handleSubmit} className="space-y-4">
          {/* Current Password */}
          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('changePassword.currentLabel')}
            </label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 pr-10 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 ${errors.currentPassword ? 'border-red-400 dark:border-red-500/70' : 'border-gray-300 dark:border-gray-600'
                  }`}
                placeholder={t('changePassword.currentPlaceholder')}
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                disabled={isSubmitting}
              >
                {showPasswords.current ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {errors.currentPassword && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.currentPassword}</p>
            )}
          </div>

          {/* New Password */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('changePassword.newLabel')}
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 pr-10 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 ${errors.newPassword ? 'border-red-400 dark:border-red-500/70' : 'border-gray-300 dark:border-gray-600'
                  }`}
                placeholder={t('changePassword.newPlaceholder')}
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                disabled={isSubmitting}
              >
                {showPasswords.new ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {errors.newPassword && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.newPassword}</p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('changePassword.newComplexity')}
            </p>
          </div>

          {/* Confirm New Password */}
          <div>
            <label htmlFor="confirmNewPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('changePassword.confirmLabel')}
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                id="confirmNewPassword"
                name="confirmNewPassword"
                value={formData.confirmNewPassword}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 pr-10 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 ${errors.confirmNewPassword ? 'border-red-400 dark:border-red-500/70' : 'border-gray-300 dark:border-gray-600'
                  }`}
                placeholder={t('changePassword.confirmPlaceholder')}
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                disabled={isSubmitting}
              >
                {showPasswords.confirm ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {errors.confirmNewPassword && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.confirmNewPassword}</p>
            )}
          </div>

          {showInlineActions && (
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-6 py-2.5 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm dark:shadow-none"
              >
                {t('changePassword.submit')}
              </button>

              {onCancel && (
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="px-6 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 font-medium border border-gray-300 dark:border-gray-600"
                >
                  {t('changePassword.cancel')}
                </button>
              )}
            </div>
          )}
        </form>

        {/* Security Tips */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950/50 border border-blue-100 dark:border-blue-800/60 rounded-lg">
          <h3 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
            {t('changePassword.securityTipsTitle')}
          </h3>
          <ul className="text-xs text-blue-800 dark:text-blue-200/90 space-y-1">
            <li>• {t('changePassword.securityTip1')}</li>
            <li>• {t('changePassword.securityTip2')}</li>
            <li>• {t('changePassword.securityTip3')}</li>
            <li>• {t('changePassword.securityTip4')}</li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default PasswordChangeForm;